/** Default maximum number of retry attempts */
const DEFAULT_MAX_RETRIES = 100

/** Delay multiplier in milliseconds for retry attempts (3 seconds) */
const RETRY_DELAY_MULTIPLIER = 3000 // 3 seconds

/**
 * Determines if an error is retriable based on its message content.
 * @param error - The error to check
 * @returns True if the error contains '401' status, indicating it's retriable
 */
const isRetriable401Error = (error: unknown): boolean => {
  return error instanceof Error && error.message.includes('Status: 401')
}

/**
 * Creates a promise that resolves after the specified delay.
 * @param ms - The delay duration in milliseconds
 * @returns A promise that resolves after the delay
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Executes a function with retry logic for 401 errors.
 * Implements exponential backoff with configurable retry attempts.
 * @template T - The return type of the function being retried
 * @param fn - The function to execute with retry logic
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns The result of the function execution
 * @throws The original error if it's not retriable or max retries exceeded
 */
export const retryWith401Handler = async <T>(
  fn: () => Promise<T>,
  maxRetries = DEFAULT_MAX_RETRIES,
): Promise<T> => {
  let retryCount = 0

  while (retryCount <= maxRetries) {
    try {
      return await fn()
    } catch (error) {
      if (retryCount < maxRetries && isRetriable401Error(error)) {
        retryCount += 1
        const delayMs = retryCount * RETRY_DELAY_MULTIPLIER
        await sleep(delayMs)
        continue
      }
      throw error
    }
  }

  throw new Error('Maximum retry attempts reached')
}
